import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircleRounded as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { format, formatDistanceStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { api_nocors } from '../../services/api';
import copyText from '../../utils/copyText';

const formatDate = (d) => {
  if (!d) return null;
  try { return format(new Date(d), "dd/MM/yyyy HH:mm", { locale: ptBR }); } catch { return d; }
};

const formatDuration = (a, b) => {
  if (!a || !b) return null;
  try { return formatDistanceStrict(new Date(b), new Date(a), { locale: ptBR }); } catch { return null; }
};

const parseTags = (tags) => {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    try { return JSON.parse(tags) || []; } catch { return []; }
  }
  return [];
};

const Mono = styled('span')({
  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  fontSize: '0.72rem',
  letterSpacing: '0.02em',
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.75),
}));

const KV = ({ label, children, empty }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontSize: '0.58rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        lineHeight: 1.1,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        fontSize: '0.78rem',
        fontWeight: 500,
        lineHeight: 1.35,
        color: empty ? 'text.disabled' : 'text.primary',
        fontStyle: empty ? 'italic' : 'normal',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Swatch = ({ color }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '2px',
      mr: 0.625,
      bgcolor: color || 'transparent',
      border: '1px solid rgba(0,0,0,0.18)',
      verticalAlign: 'middle',
    }}
  />
);

const Tag = ({ tag }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.625,
      py: '1px',
      mr: 0.375,
      mb: 0.375,
      borderRadius: 0.5,
      fontSize: '0.65rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: tag.color || 'text.primary',
      bgcolor: tag.color ? alpha(tag.color, 0.1) : 'action.selected',
      border: (t) => `1px solid ${tag.color ? alpha(tag.color, 0.35) : alpha(t.palette.divider, 0.7)}`,
    }}
  >
    <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tag.color || 'currentColor' }} />
    {tag.title}
  </Box>
);

const Person = ({ name, role }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.625, mr: 0.5, mb: 0.5 }}>
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
        color: 'primary.main',
        fontSize: '0.6rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {(name || '?').slice(0, 1).toUpperCase()}
    </Box>
    <Box sx={{ lineHeight: 1.1 }}>
      <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', lineHeight: 1.1 }}>
        {name}
      </Typography>
      {role && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.55rem',
            color: 'text.disabled',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {role}
        </Typography>
      )}
    </Box>
  </Box>
);

const TimelineNode = ({ label, value, duration, color, active, isFirst, isLast }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', columnGap: 1.25, position: 'relative' }}>
    {/* Eixo + ponto */}
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      {!isFirst && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: '50%',
            width: 2,
            bgcolor: (t) => alpha(active ? color : t.palette.divider, active ? 0.45 : 1),
          }}
        />
      )}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            bottom: 0,
            width: 2,
            bgcolor: (t) => alpha(active ? color : t.palette.divider, active ? 0.45 : 1),
          }}
        />
      )}
      <Box
        sx={{
          position: 'relative',
          mt: '6px',
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: active ? color : 'background.paper',
          border: `2px solid ${active ? color : alpha(color, 0.45)}`,
          boxShadow: active ? `0 0 0 3px ${alpha(color, 0.18)}` : 'none',
          zIndex: 1,
        }}
      />
    </Box>

    {/* Conteúdo */}
    <Box sx={{ pb: 1.25, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: active ? color : 'text.disabled',
          }}
        >
          {label}
        </Typography>
        {duration && (
          <Typography
            variant="caption"
            sx={{ fontSize: '0.58rem', color: 'text.disabled', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            +{duration}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.78rem',
          fontWeight: active ? 600 : 400,
          color: active ? 'text.primary' : 'text.disabled',
          fontStyle: active ? 'normal' : 'italic',
          lineHeight: 1.3,
        }}
      >
        {value ? <Mono style={{ fontSize: '0.78rem' }}>{value}</Mono> : 'Pendente'}
      </Typography>
    </Box>
  </Box>
);

const Checklist = ({ task }) => {
  const total = task.checklists?.length || 0;
  const done = (task.checklists || []).filter((c) => c.isCompleted).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const completed = total > 0 && done === total;
  const accent = completed ? '#2e7d32' : '#1976d2';

  return (
    <Box
      sx={{
        mb: 1,
        border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
        borderRadius: 0.5,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: (t) => alpha(t.palette.background.default, 0.5),
          borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word', minWidth: 0 }}>
          {task.title || 'Sem título'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>
            {done}/{total}
          </Typography>
          <Box sx={{ width: 60, height: 4, bgcolor: (t) => alpha(t.palette.divider, 0.5), borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: accent, transition: 'width 0.2s' }} />
          </Box>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: accent, minWidth: 28, textAlign: 'right' }}>
            {pct}%
          </Typography>
        </Box>
      </Box>
      <Box>
        {(task.checklists || []).map((item) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.875,
              px: 1.25,
              py: 0.5,
              '&:not(:last-of-type)': {
                borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.35)}`,
              },
            }}
          >
            {item.isCompleted ? (
              <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', mt: '2px', flexShrink: 0 }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                fontSize: '0.74rem',
                lineHeight: 1.4,
                color: item.isCompleted ? 'text.disabled' : 'text.primary',
                textDecoration: item.isCompleted ? 'line-through' : 'none',
                wordBreak: 'break-word',
              }}
            >
              {item.description || <em style={{ opacity: 0.6 }}>(sem descrição)</em>}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default function PublicCardModal({ open, onClose, linkId, cardId }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !linkId || !cardId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCard(null);

    api_nocors
      .get(`/public/k/${linkId}/card/${cardId}`)
      .then((res) => { if (!cancelled) setCard(res.data); })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Não foi possível carregar este card.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, linkId, cardId]);

  const tags = card ? parseTags(card.tags) : [];

  const protocol = useMemo(() => {
    if (!card) return null;
    if (card.taskNumber) return `#${String(card.taskNumber).padStart(6, '0')}`;
    return card.id ? `#${String(card.id).slice(-8).toUpperCase()}` : null;
  }, [card]);

  const handleCopy = async () => {
    if (!card?.id) return;
    await copyText(card.id, 'Identificador copiado');
  };

  const durations = useMemo(() => {
    if (!card) return {};
    return {
      started: formatDuration(card.createdAt, card.startedAt),
      finished: formatDuration(card.startedAt, card.finishedAt),
      concluded: formatDuration(card.finishedAt, card.concludedAt),
    };
  }, [card]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          maxWidth: 460,
        },
      }}
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      )}

      {!loading && !error && card && (
        <>
          {/* Cabeçalho compacto */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              bgcolor: (t) => alpha(t.palette.background.default, 0.6),
              borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                Ficha do Cartão
              </Typography>
              {protocol && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                  <Mono style={{ fontWeight: 700, color: 'inherit' }}>{protocol}</Mono>
                  <Tooltip title="Copiar ID">
                    <IconButton size="small" onClick={handleCopy} sx={{ p: 0.125 }}>
                      <ContentCopyIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Body compacto */}
          <Box sx={{ px: 2, py: 1.5, maxHeight: '78vh', overflowY: 'auto' }}>
            {/* Título */}
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                lineHeight: 1.25,
                wordBreak: 'break-word',
                mb: 1,
              }}
            >
              {card.title}
            </Typography>

            {/* Faixa de status: coluna · prioridade · tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.625, mb: 1.5 }}>
              {card.column && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.625,
                    py: '1px',
                    borderRadius: 0.5,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: card.column.color || 'text.primary',
                    bgcolor: card.column.color ? alpha(card.column.color, 0.1) : 'action.selected',
                    border: (t) => `1px solid ${card.column.color ? alpha(card.column.color, 0.35) : alpha(t.palette.divider, 0.7)}`,
                  }}
                >
                  <Swatch color={card.column.color} />
                  {card.column.title}
                </Box>
              )}
              {card.kanbanPriorityLevel && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.625,
                    py: '1px',
                    borderRadius: 0.5,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: card.kanbanPriorityLevel.color || 'text.primary',
                    bgcolor: card.kanbanPriorityLevel.color ? alpha(card.kanbanPriorityLevel.color, 0.12) : 'action.selected',
                    border: (t) => `1px solid ${card.kanbanPriorityLevel.color ? alpha(card.kanbanPriorityLevel.color, 0.4) : alpha(t.palette.divider, 0.7)}`,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  <Swatch color={card.kanbanPriorityLevel.color} />
                  {card.kanbanPriorityLevel.title}
                </Box>
              )}
              {tags.map((tag) => <Tag key={tag.id} tag={tag} />)}
            </Box>

            {/* Pessoas + Descrição lado a lado em xs+ */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 1.25, mb: 1.5 }}>
              <KV label="Criado por" empty={!card.creator}>
                {card.creator ? <Person name={card.creator.name} role="Autor" /> : '—'}
              </KV>
              <KV label="Atribuídos" empty={(card.assigned || []).length === 0}>
                {(card.assigned || []).length === 0
                  ? '—'
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      {card.assigned.map((u) => <Person key={u.id} name={u.name} />)}
                    </Box>}
              </KV>
            </Box>

            {/* Descrição */}
            {card.description && (
              <Box sx={{ mb: 1.5 }}>
                <SectionLabel>Descrição</SectionLabel>
                <Box
                  sx={{
                    p: 1.25,
                    borderLeft: (t) => `2px solid ${t.palette.text.primary}`,
                    bgcolor: (t) => alpha(t.palette.background.default, 0.5),
                  }}
                >
                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                    {card.description}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Linha do tempo (vertical real) */}
            <Box sx={{ mb: 1.5 }}>
              <SectionLabel>Linha do tempo</SectionLabel>
              <Box>
                <TimelineNode
                  label="Criação"
                  value={formatDate(card.createdAt)}
                  color="#1976d2"
                  active={!!card.createdAt}
                  isFirst
                />
                <TimelineNode
                  label="Início"
                  value={formatDate(card.startedAt)}
                  duration={durations.started}
                  color="#ed6c02"
                  active={!!card.startedAt}
                />
                <TimelineNode
                  label="Finalização"
                  value={formatDate(card.finishedAt)}
                  duration={durations.finished}
                  color="#2e7d32"
                  active={!!card.finishedAt}
                />
                <TimelineNode
                  label="Conclusão"
                  value={formatDate(card.concludedAt)}
                  duration={durations.concluded}
                  color="#0288d1"
                  active={!!card.concludedAt}
                  isLast
                />
              </Box>
            </Box>

            {/* Checklists */}
            {(card.tasks || []).length > 0 && (
              <Box sx={{ mb: 1 }}>
                <SectionLabel>
                  Checklists{' '}
                  <Box component="span" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                    ({card.tasks.length})
                  </Box>
                </SectionLabel>
                {card.tasks.map((task) => <Checklist key={task.id} task={task} />)}
              </Box>
            )}

            {/* Rodapé */}
            <Box
              sx={{
                mt: 1.5,
                pt: 1,
                borderTop: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled', letterSpacing: '0.04em' }}>
                Modo somente leitura
              </Typography>
              <Mono style={{ fontSize: '0.6rem', color: 'inherit', opacity: 0.5 }}>
                {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
              </Mono>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  );
}
